import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { CKEditor, useCKEditorCloud } from '@ckeditor/ckeditor5-react';
import { useTranslation } from '../../redux/TranslationHelper';
import './HtmlEditor.css';

// 커스텀 업로드 어댑터 클래스
class CustomUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file
      .then(file => new Promise((resolve, reject) => {
        this._initRequest();
        this._initListeners(resolve, reject, file);
        this._sendRequest(file);
      }));
  }

  abort() {
    if (this.xhr) {
      this.xhr.abort();
    }
  }

  _initRequest() {
    const xhr = this.xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://refrigerator.logipasta.com/v1/file', true);
    xhr.responseType = 'json';
  }

  _initListeners(resolve, reject, file) {
    const xhr = this.xhr;
    const loader = this.loader;
    const genericErrorText = `파일을 업로드할 수 없습니다: ${file.name}.`;

    xhr.addEventListener('error', () => reject(genericErrorText));
    xhr.addEventListener('abort', () => reject());
    xhr.addEventListener('load', () => {
      const response = xhr.response;

      if (!response || response.error) {
        return reject(response && response.error ? response.error.message : genericErrorText);
      }

      // refrigerator API 응답 형식에 맞게 수정
      if (response.file) {
        resolve({
          default: response.file
        });
      } else {
        reject('응답에 파일 URL이 없습니다.');
      }
    });

    if (xhr.upload) {
      xhr.upload.addEventListener('progress', evt => {
        if (evt.lengthComputable) {
          loader.uploadTotal = evt.total;
          loader.uploaded = evt.loaded;
        }
      });
    }
  }

  _sendRequest(file) {
    const data = new FormData();
    data.append('file', file);
    data.append('bucket', 'withcookie-bucket');
    data.append('path', 'c/default');

    this.xhr.send(data);
  }
}

const HtmlEditor = ({ initialData, onChange, placeholder = '여기에 내용을 입력하세요...' }) => {
  const { t } = useTranslation();
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  
  // CKEditor Cloud 초기화 (클라우드 방식)
  const cloud = useCKEditorCloud({ 
    version: '45.1.0', 
    premium: true, 
    translations: ['ko'] 
  });

  // DOM 레이아웃이 완료된 후 실행
  useLayoutEffect(() => {
    // DOM이 완전히 렌더링된 후 지연을 두고 레이아웃 준비 상태로 설정
    const timer = setTimeout(() => {
      setIsLayoutReady(true);
    }, 200); // 200ms 지연

    return () => {
      clearTimeout(timer);
      setIsLayoutReady(false);
    };
  }, []);

  // Cloud와 Layout이 모두 준비되면 에디터 렌더링 허용
  useEffect(() => {
    if (cloud.status === 'success' && isLayoutReady) {
      // 추가 지연을 두고 에디터 렌더링 허용
      const timer = setTimeout(() => {
        setIsEditorReady(true);
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      setIsEditorReady(false);
    }
  }, [cloud.status, isLayoutReady]);

  // 에디터 설정 및 플러그인 구성
  const { ClassicEditor, editorConfig } = useMemo(() => {
    if (!isEditorReady || cloud.status !== 'success') {
      return {};
    }

    const {
      ClassicEditor,
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      CloudServices,
      Code,
      CodeBlock,
      Essentials,
      FindAndReplace,
      Font,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      HtmlComment,
      HtmlEmbed,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      PageBreak,
      Paragraph,
      PasteFromOffice,
      RemoveFormat,
      ShowBlocks,
      SimpleUploadAdapter,
      Strikethrough,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      Underline
    } = cloud.CKEditor;
    
    const { PasteFromOfficeEnhanced, SourceEditingEnhanced } = cloud.CKEditorPremiumFeatures;

    return {
      ClassicEditor,
      editorConfig: {
        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'sourceEditingEnhanced',
            'showBlocks',
            'findAndReplace',
            '|',
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'code',
            'removeFormat',
            '|',
            'fontSize',
            'fontFamily',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'alignment',
            '|',
            'link',
            'insertImage',
            'mediaEmbed',
            'insertTable',
            'blockQuote',
            'codeBlock',
            'htmlEmbed',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            '|',
            'outdent',
            'indent',
            '|',
            'highlight',
            'horizontalLine',
            'pageBreak'
          ],
          shouldNotGroupWhenFull: false
        },
        plugins: [
          Alignment,
          Autoformat,
          AutoImage,
          AutoLink,
          Autosave,
          BlockQuote,
          Bold,
          CloudServices,
          Code,
          CodeBlock,
          Essentials,
          FindAndReplace,
          Font,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HorizontalLine,
          HtmlComment,
          HtmlEmbed,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          MediaEmbed,
          PageBreak,
          Paragraph,
          PasteFromOffice,
          PasteFromOfficeEnhanced,
          RemoveFormat,
          ShowBlocks,
          SimpleUploadAdapter,
          SourceEditingEnhanced,
          Strikethrough,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          TextTransformation,
          TodoList,
          Underline
        ],
        // 커스텀 업로드 어댑터 설정
        extraPlugins: [
          function CustomUploadAdapterPlugin(editor) {
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
              return new CustomUploadAdapter(loader);
            };
          }
        ],
        heading: {
          options: [
            {
              model: 'paragraph',
              title: 'Paragraph',
              class: 'ck-heading_paragraph'
            },
            {
              model: 'heading1',
              view: 'h1',
              title: 'Heading 1',
              class: 'ck-heading_heading1'
            },
            {
              model: 'heading2',
              view: 'h2',
              title: 'Heading 2',
              class: 'ck-heading_heading2'
            },
            {
              model: 'heading3',
              view: 'h3',
              title: 'Heading 3',
              class: 'ck-heading_heading3'
            },
            {
              model: 'heading4',
              view: 'h4',
              title: 'Heading 4',
              class: 'ck-heading_heading4'
            },
            {
              model: 'heading5',
              view: 'h5',
              title: 'Heading 5',
              class: 'ck-heading_heading5'
            },
            {
              model: 'heading6',
              view: 'h6',
              title: 'Heading 6',
              class: 'ck-heading_heading6'
            }
          ]
        },
        htmlSupport: {
          allow: [
            {
              name: /^.*$/,
              styles: true,
              attributes: true,
              classes: true
            }
          ]
        },
        image: {
          toolbar: [
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'resizeImage'
          ]
        },
        initialData: initialData || '',
        language: 'ko',
        licenseKey: process.env.REACT_APP_CKEDITOR5_LICENSE_KEY,
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
          decorators: {
            toggleDownloadable: {
              mode: 'manual',
              label: 'Downloadable',
              attributes: {
                download: 'file'
              }
            }
          }
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true
          }
        },
        placeholder: placeholder,
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        },
        fontFamily: {
          options: [
            'default',
            'Arial, Helvetica, sans-serif',
            'Courier New, Courier, monospace',
            'Georgia, serif',
            'Lucida Sans Unicode, Lucida Grande, sans-serif',
            'Tahoma, Geneva, sans-serif',
            'Times New Roman, Times, serif',
            'Trebuchet MS, Helvetica, sans-serif',
            'Verdana, Geneva, sans-serif',
            '맑은 고딕, Malgun Gothic, sans-serif',
            '나눔고딕, NanumGothic, sans-serif',
            '돋움, Dotum, sans-serif',
            '굴림, Gulim, sans-serif'
          ],
          supportAllValues: true
        },
        fontSize: {
          options: [
            10, 12, 14, 'default', 18, 20, 22, 24, 26, 28, 30, 32, 34, 36,
            40, 44, 48, 52, 56, 60, 64, 68, 72, 76, 80, 84, 88, 92, 96, 100
          ],
          supportAllValues: true
        },
        fontColor: {
          columns: 5,
          documentColors: 10
        },
        fontBackgroundColor: {
          columns: 5,
          documentColors: 10
        },
        mediaEmbed: {
          previewsInData: true
        }
      }
    };
  }, [isEditorReady, cloud, initialData, placeholder]);

  // 에디터 변경 사항 처리
  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    if (onChange) {
      onChange(data);
    }
  };

  // 에디터 준비 완료 처리
  const handleEditorReady = (editor) => {
    editorRef.current = editor;
  };

  // 로딩 상태 표시
  if (cloud.status === 'loading' || !isLayoutReady || !isEditorReady) {
    return (
      <div className="ckeditor-loading">
        <div className="loading-spinner"></div>
        <p>{t('에디터를 로딩 중입니다...')}</p>
      </div>
    );
  }

  // 에러 상태 표시
  if (cloud.status === 'error') {
    return (
      <div className="ckeditor-error">
        <p>{t('에디터 로딩에 실패했습니다.')}</p>
        <p>{cloud.error?.message}</p>
      </div>
    );
  }

  return (
    <div className="ckeditor-cloud-container" ref={editorContainerRef}>
      <div className="ckeditor-cloud-editor">
        {ClassicEditor && editorConfig && (
          <CKEditor
            editor={ClassicEditor}
            config={editorConfig}
            onReady={handleEditorReady}
            onChange={handleEditorChange}
          />
        )}
      </div>
    </div>
  );
};

export default HtmlEditor; 